import { FC, useCallback, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { ProductScoringSection } from "./ProductScoringSection";
import { ConditionSelectFilter } from "@/components/filters/Condition/ConditionSelectFilter";
import { FaiFilterSection } from "@/components/filters/UserFilters/FaiFilter/FaiFilterSection";
import type { FilterMode } from "@/components/filters/Condition/types";
import type {
  ScoringType,
  ProductPresenceBand,
  InclusionMethod,
} from "@/lib/scoring/types";

// ── Exports for criterion registry ────────────────────────────────────────

export const initialBand: ProductPresenceBand = {
  ids: [],
  inclusionMethod: "ANY_PRESENT",
  score: 0,
};

export function hasErrors(bands: unknown[]): boolean {
  const typed = bands as ProductPresenceBand[];
  if (typed.length === 0) return true;
  return typed.some((band, i) => {
    const others = typed.filter((_, j) => j !== i);
    return getBandError(band, others) !== null;
  });
}

export function getBandSummary(band: unknown): string {
  const b = band as ProductPresenceBand;
  const count = b.ids.length;
  if (count === 0) return "—";
  const method = b.inclusionMethod === "ALL_PRESENT" ? "all" : "any";
  return `Has ${method} of ${count} product${count !== 1 ? "s" : ""}`;
}

// ── Validation ────────────────────────────────────────────────────────────

function getBandError(
  band: ProductPresenceBand,
  others: ProductPresenceBand[]
): string | null {
  if (band.ids.length === 0) return "You must select at least one product";
  if (!band.score || band.score < 0) return "Points can't be empty";

  const sorted = [...band.ids].sort();
  const hasDuplicate = others.some((o) => {
    const otherSorted = [...o.ids].sort();
    return (
      sorted.length === otherSorted.length &&
      sorted.every((id, idx) => id === otherSorted[idx])
    );
  });
  if (hasDuplicate) return "Products can't be the same across bands";

  return null;
}

// ── Component ─────────────────────────────────────────────────────────────

interface Props {
  criterion: ScoringType;
  onChange: (updated: ScoringType) => void;
}

export const ProductPresenceEditor: FC<Props> = ({ criterion, onChange }) => {
  const bands = criterion.products ?? [];

  // Stable keys for React reconciliation — avoids stale state in
  // ProductScoringSection when bands are deleted/reordered.
  const nextId = useRef(bands.length);
  const [bandKeys] = useState(() => bands.map((_, i) => i));
  const getBandKey = (index: number) => {
    while (bandKeys.length <= index) {
      bandKeys.push(nextId.current++);
    }
    return bandKeys[index];
  };

  const updateBand = useCallback(
    (index: number, patch: Partial<ProductPresenceBand>) => {
      onChange({
        ...criterion,
        products: bands.map((b, i) => (i === index ? { ...b, ...patch } : b)),
      });
    },
    [bands, criterion, onChange]
  );

  const removeBand = useCallback(
    (index: number) => {
      bandKeys.splice(index, 1);
      onChange({
        ...criterion,
        products: bands.filter((_, i) => i !== index),
      });
    },
    [bands, bandKeys, criterion, onChange]
  );

  const addBand = useCallback(() => {
    bandKeys.push(nextId.current++);
    onChange({
      ...criterion,
      products: [...bands, { ...initialBand }],
    });
  }, [bands, bandKeys, criterion, onChange]);

  return (
    <Box sx={{ px: 2 }}>
      {bands.map((band, i) => {
        const others = bands.filter((_, j) => j !== i);
        const isDirty = band.ids.length > 0 || band.score !== 0;
        const error = isDirty ? getBandError(band, others) : null;
        const faiSelectedIds = [
          ...(band.departmentIds ?? []),
          ...(band.roleIds ?? []),
        ];

        return (
          <Box
            key={getBandKey(i)}
            sx={{
              border: "1px solid",
              borderColor: error ? "error.main" : "divider",
              borderRadius: 1,
              p: 1.5,
              mb: 1,
            }}
          >
            <ProductScoringSection
              productIds={band.ids}
              onProductsChange={(productIds) =>
                updateBand(i, { ids: productIds })
              }
            />

            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ my: 1 }}
            >
              <Typography variant="body2">If</Typography>
              <ConditionSelectFilter
                value={band.inclusionMethod as FilterMode}
                onChange={(mode) =>
                  updateBand(i, { inclusionMethod: mode as InclusionMethod })
                }
                id={`product-presence-condition-${i}`}
              />
              <Typography variant="body2">
                of the products are installed
              </Typography>
            </Stack>

            <FaiFilterSection
              id={`product-presence-fai-${i}`}
              disabled={band.ids.length === 0}
              selectedIds={faiSelectedIds}
              onFaiSelect={(departmentIds, roleIds) =>
                updateBand(i, { departmentIds, roleIds })
              }
            />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 1,
                mt: 1,
              }}
            >
              <TextField
                size="small"
                type="number"
                label="Pts"
                value={band.score === 0 ? "" : band.score}
                error={error === "Points can't be empty"}
                onChange={(e) =>
                  updateBand(i, {
                    score: e.target.value === "" ? 0 : Number(e.target.value),
                  })
                }
                inputProps={{
                  min: 0,
                  step: 1,
                  "data-testid": `scoring-product-presence-pts-${i}-input`,
                }}
                sx={{ width: 80 }}
              />
              <IconButton
                size="small"
                onClick={() => removeBand(i)}
                aria-label="Remove band"
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>

            {error && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.5 }}
              >
                {error}
              </Typography>
            )}
          </Box>
        );
      })}

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addBand}
        sx={{ color: "primary.main", fontWeight: 600 }}
        data-testid="scoring-product-presence-add-btn"
      >
        ADD BAND
      </Button>
    </Box>
  );
};
